/**
 * Zoe answer feedback + Truth Layer question log — mirrors
 * zeecore/src/agents/harness/conversation/{answerFeedbackStore,questionLog}.ts.
 */

export type AnswerRating = 'up' | 'down';

export const ANSWER_FEEDBACK_REASONS = [
  'incorrect',
  'incomplete',
  'wrong_entity',
  'not_relevant',
  'unclear',
  'other',
] as const;
export type AnswerFeedbackReason = (typeof ANSWER_FEEDBACK_REASONS)[number];

export const ANSWER_FEEDBACK_REASON_LABELS: Record<AnswerFeedbackReason, string> = {
  incorrect: 'Incorrect',
  incomplete: 'Incomplete',
  wrong_entity: 'Wrong order / vehicle / driver',
  not_relevant: "Didn't answer the question",
  unclear: 'Hard to understand',
  other: 'Other',
};

export interface AnswerFeedbackRating {
  rating: AnswerRating;
  reasons: AnswerFeedbackReason[];
  comment?: string;
  submitted_by: string;
  submitted_by_label?: string;
  submitted_at: string;
  answer_seen?: string;
}

export interface AnswerFeedbackNote {
  note_id: string;
  text: string;
  author_id: string;
  author_label?: string;
  created_at: string;
}

export type QuestionLogErrorKind =
  | 'investigation_failed'
  | 'narration_failed'
  | 'narration_disabled'
  | 'answer_contract_violation';

export interface QuestionLogError {
  kind: QuestionLogErrorKind;
  /** What the user was shown. */
  message: string;
  detail?: string;
  endpoint: 'query' | 'query/stream';
  http_status?: number;
  duration_ms: number;
}

/**
 * Cost/latency for one answered question. Mirrors `TurnUsage` in
 * zeecore/src/agents/harness/conversation/types.ts.
 *
 * `duration_ms` is wall-clock — what the user waited. Token counts cover every
 * billed call in the run, narration included. `total_tokens` is prompt +
 * completion; cached tokens are metered separately, so the billed figure is
 * `total_tokens + cache_read_tokens + cache_write_tokens` (see `billedTokens`).
 */
export interface TurnUsage {
  duration_ms: number;
  total_tokens: number;
  prompt_tokens: number;
  completion_tokens: number;
  llm_latency_ms: number;
  cache_read_tokens: number;
  cache_write_tokens: number;
}

export interface QuestionLogEntry {
  organisation_id: string;
  user_id: string;
  user_label?: string;
  /** Position in its conversation, for ordering a thread. Absent on older turns. */
  seq?: number;
  /** Set when this question was a reply to a clarification, picked from its options. */
  answered_clarification?: { clarification_id: string; option_id: string; label: string };
  conversation_id: string;
  turn_id: string;
  run_id: string;
  asked_at: string;
  question: string;
  effective_question?: string;
  headline: string;
  answer_text?: string;
  conclusion?: string;
  confidence?: string;
  claim_digest: string[];
  agent_id?: string;
  status: 'answered' | 'needs_clarification' | 'error';
  error?: QuestionLogError;
  feedback?: AnswerFeedbackRating;
  notes: AnswerFeedbackNote[];
  /** Absent on turns answered before usage was recorded. */
  usage?: TurnUsage;
  turn_source: 'conversation' | 'feedback_snapshot' | 'failure_record';
}

/** One conversation in the log — for grouping rows into threads and showing its cost once. */
export interface QuestionLogConversation {
  conversation_id: string;
  user_id: string;
  user_label?: string;
  /** The conversation's first question. */
  title: string;
  /** Every question it has had — more than the log shows if some fall outside the period. */
  turn_count: number;
  updated_at: string;
  /** Cost and time across the whole conversation's life, not just the period shown. */
  usage_total?: TurnUsage & { turns: number };
}

export interface QuestionLogResponse {
  entries: QuestionLogEntry[];
  /** T30 — one line per listed conversation. Absent from servers older than T30. */
  conversations?: QuestionLogConversation[];
  /** Conversations whose full history could not be read; shown from their recent window only. */
  partial_conversations?: number;
  conversation_limit: number;
  truncated: boolean;
  /** More errored questions exist in the window than were returned. */
  failures_truncated?: boolean;
  generated_at: string;
}
